import React, { useState } from "react";
import { IonLoading } from "@ionic/react";
import { useTranslation } from "react-i18next";
import { updateProfile } from "firebase/auth";
import { auth } from "../firebase";
import { createParentProfile, createProfile, ProfileRole } from "../services/progressService";
import { track } from "../services/analytics";
import { YEARS, Year } from "../content/types";
import { PARENT_AVATARS, STUDENT_AVATARS } from "../utils/avatars";
import AuthShell, { inputClass, labelClass } from "./AuthShell";
import { logout } from "../utils/session";

/**
 * Première expérience (PDF §4) : élève → prénom + année scolaire (9e/10e/11e) + objectif ;
 * parent (PDF §14) → prénom + avatar uniquement, le rôle est enregistré dans le profil.
 * L'année personnalise le parcours ; l'élève garde accès aux autres années.
 */
const ProfileSetup: React.FC = () => {
  const { t } = useTranslation();
  const user = auth.currentUser;
  const [role, setRole] = useState<ProfileRole>("student");
  const [name, setName] = useState(user?.displayName?.split(" ")[0] || "");
  const [year, setYear] = useState<Year | null>(null);
  const [avatar, setAvatar] = useState(STUDENT_AVATARS[0]);
  const [goal, setGoal] = useState(15);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isParent = role === "parent";
  const avatars = isParent ? PARENT_AVATARS : STUDENT_AVATARS;

  const chooseRole = (r: ProfileRole) => {
    setRole(r);
    setAvatar((r === "parent" ? PARENT_AVATARS : STUDENT_AVATARS)[0]);
  };

  const submit = async () => {
    if (!user) return;
    const trimmed = name.trim();
    if (trimmed.length < 2 || trimmed.length > 20) return setError(t("profileSetup.nameInvalid"));
    if (!isParent && !year) return setError(t("profileSetup.yearRequired"));
    setSaving(true);
    try {
      if (isParent) await createParentProfile(user.uid, { displayName: trimmed, avatar });
      else await createProfile(user.uid, { displayName: trimmed, year: year as Year, dailyGoalMinutes: goal, avatar });
      if (user.displayName !== trimmed) await updateProfile(user, { displayName: trimmed });
      track("profile_completed", { role });
      // Élève : questionnaire « façon d'apprendre » (Phase 3b) ; parent : /home.
      // Le guard de App (profil présent, profil d'apprentissage) fait autorité dans les deux cas.
      window.location.href = isParent ? "/home" : "/learning-profile";
    } catch (e) {
      setError(t("common.errorGeneric"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AuthShell
      title={isParent ? t("profileSetup.parentTitle") : t("profileSetup.title")}
      subtitle={isParent ? t("profileSetup.parentSubtitle") : t("profileSetup.subtitle")}
      icon={isParent ? "fa-people-roof" : "fa-id-badge"}
      alert={{
        open: Boolean(error),
        header: t("common.information"),
        message: error || "",
        onClose: () => setError(null),
        dismissLabel: t("common.close"),
      }}>
      <IonLoading isOpen={saving} message={t("common.saving")} />
      <div className="space-y-5">
        <div className="space-y-2">
          <label className={labelClass}>
            <i className="fa-solid fa-user-group text-primary-400" />
            {t("profileSetup.whoAreYou")}
          </label>
          <div className="grid grid-cols-2 gap-2">
            {(["student", "parent"] as ProfileRole[]).map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => chooseRole(r)}
                className={`py-3 rounded-xl font-bold border-2 transition flex items-center justify-center gap-2 ${
                  role === r ? "bg-primary-500 border-primary-400 text-white" : "bg-white/5 border-white/15 text-gray-200"
                }`}>
                <i className={`fa-solid ${r === "parent" ? "fa-people-roof" : "fa-graduation-cap"}`} />
                {t(r === "parent" ? "profileSetup.roleParent" : "profileSetup.roleStudent")}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <label className={labelClass}>
            <i className="fa-solid fa-user text-primary-400" />
            {isParent ? t("profileSetup.firstNameParent") : t("profileSetup.firstName")}
          </label>
          <input
            type="text"
            value={name}
            maxLength={20}
            onChange={(e) => setName(e.target.value)}
            placeholder={isParent ? "Sophie" : "Lucas"}
            className={inputClass}
          />
        </div>

        <div className="space-y-2">
          <label className={labelClass}>
            <i className="fa-solid fa-face-smile text-primary-400" />
            {isParent ? t("profileSetup.avatarParent") : t("profileSetup.avatar")}
          </label>
          <div className="grid grid-cols-8 gap-1">
            {avatars.map((a) => (
              <button
                key={a}
                type="button"
                onClick={() => setAvatar(a)}
                className={`text-2xl rounded-xl py-1 transition ${avatar === a ? "bg-primary-500/40 ring-2 ring-primary-400" : "bg-white/5"}`}>
                {a}
              </button>
            ))}
          </div>
        </div>

        {!isParent && (
          <>
            <div className="space-y-2">
              <label className={labelClass}>
                <i className="fa-solid fa-school text-primary-400" />
                {t("profileSetup.year")}
              </label>
              <div className="grid grid-cols-3 gap-2">
                {YEARS.map((y) => (
                  <button
                    key={y}
                    type="button"
                    onClick={() => setYear(y)}
                    className={`py-3 rounded-xl font-bold border-2 transition ${
                      year === y ? "bg-primary-500 border-primary-400 text-white" : "bg-white/5 border-white/15 text-gray-200"
                    }`}>
                    {t("common.yearShort", { year: y })}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-400 ml-1">{t("profileSetup.yearHint")}</p>
            </div>

            <div className="space-y-2">
              <label className={labelClass}>
                <i className="fa-solid fa-bullseye text-primary-400" />
                {t("profileSetup.dailyGoal")}
              </label>
              <div className="grid grid-cols-4 gap-2">
                {[10, 15, 20, 30].map((g) => (
                  <button
                    key={g}
                    type="button"
                    onClick={() => setGoal(g)}
                    className={`py-2 rounded-xl text-sm font-bold border-2 ${
                      goal === g ? "bg-success-600 border-success-400 text-white" : "bg-white/5 border-white/15 text-gray-200"
                    }`}>
                    {g} min
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {isParent && <p className="text-xs text-gray-400 ml-1">{t("profileSetup.parentHint")}</p>}

        <button type="button" onClick={submit} className="btn-primary w-full text-base uppercase tracking-wide">
          {isParent ? t("profileSetup.parentStart") : t("profileSetup.start")}
        </button>
        <button type="button" onClick={() => logout()} className="w-full text-xs text-gray-500 py-1">
          {t("auth.verify.logout")}
        </button>
      </div>
    </AuthShell>
  );
};

export default ProfileSetup;
